import { useMemo } from "react";
import { X } from "lucide-react";
import { cn, formatCategoryDisplay } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { TagGroup, TagGroupColor } from "./types";
import { SelectedTagChip } from "./SelectedTagChip";

export interface SelectedTagsBarProps {
  /** All tag groups (used to find tag labels and colors) */
  groups: TagGroup[];
  /** Currently selected filter IDs */
  selectedFilters: string[];
  /** Callback when a filter is removed */
  onRemoveFilter: (filterId: string) => void;
  /** Callback to clear all filters */
  onClearAll: () => void;
  /** Whether to sort tags by group order (default: false) */
  sortByGroup?: boolean;
  /** Additional CSS classes */
  className?: string;
}

interface ResolvedTag {
  id: string;
  label: string;
  count: number;
  color: TagGroupColor;
  groupIndex: number;
}

/**
 * Horizontal flex-wrap display of all selected tag chips with "Clear all" button.
 *
 * Resolves filter IDs to tag labels and colors by looking up
 * in the provided tag groups. Includes a "Clear all" button at the end
 * to remove all active filters.
 *
 * @example
 * ```tsx
 * <SelectedTagsBar
 *   groups={tagGroups}
 *   selectedFilters={['project-123', 'category-Drawings']}
 *   onRemoveFilter={(id) => removeFilter(id)}
 *   onClearAll={() => clearAllFilters()}
 * />
 * ```
 */
export function SelectedTagsBar({
  groups,
  selectedFilters,
  onRemoveFilter,
  onClearAll,
  sortByGroup = false,
  className,
}: SelectedTagsBarProps) {
  const { t } = useLanguage();

  // Build a lookup map for efficient tag resolution
  const tagLookup = useMemo(() => {
    const map = new Map<string, ResolvedTag>();
    groups.forEach((group, groupIndex) => {
      group.tags.forEach((tag) => {
        // Apply formatCategoryDisplay for categories group to remove "Drawing " prefix
        const displayLabel =
          group.id === "categories" ? formatCategoryDisplay(tag.label) : tag.label;
        map.set(tag.id, {
          id: tag.id,
          label: displayLabel,
          count: tag.count,
          color: group.color,
          groupIndex,
        });
      });
    });
    return map;
  }, [groups]);

  // Resolve selected filter IDs to tag objects
  const resolvedTags = useMemo(() => {
    const tags = selectedFilters
      .map((filterId) => tagLookup.get(filterId))
      .filter((tag): tag is ResolvedTag => tag !== undefined);

    // Sort by group order if enabled (keeps tags grouped by category type)
    if (sortByGroup) {
      return tags.sort((a, b) => a.groupIndex - b.groupIndex);
    }

    return tags;
  }, [selectedFilters, tagLookup, sortByGroup]);

  if (resolvedTags.length === 0) {
    return null;
  }

  return (
    <div
      className={cn("flex flex-wrap items-center gap-2", className)}
      role="list"
      aria-label="Selected filters"
    >
      {resolvedTags.map((tag) => (
        <SelectedTagChip key={tag.id} tag={tag} color={tag.color} onRemove={onRemoveFilter} />
      ))}
      <Button
        variant="ghost"
        size="sm"
        onClick={onClearAll}
        className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
      >
        <X className="mr-1 h-3 w-3" aria-hidden="true" />
        {t.documents.tagFilter.clearAll}
      </Button>
    </div>
  );
}
